import { Search } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { InlineSpinner } from './LoadingIndicators';

export interface StorePickerOption {
  id: string;
  name: string;
  address?: string;
  city?: string;
  chainName?: string;
  storeCode?: string;
}

interface StorePickerPagination {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}

interface StorePickerProps {
  ariaLabel: string;
  value: string;
  options: StorePickerOption[];
  onChange: (value: string) => void;
  allOptionLabel?: string;
  autoOpen?: boolean;
  disabled?: boolean;
  emptyMessage?: string;
  isLoading?: boolean;
  onSearchChange?: (value: string) => void;
  pagination?: StorePickerPagination;
  placeholder?: string;
  required?: boolean;
  selectedOption?: StorePickerOption | null;
}

interface PopoverPosition {
  bottom?: number;
  left: number;
  resultsMaxHeight: number;
  top?: number;
  width: number;
}

const RESULTS_PER_PAGE = 20;

function searchableText(option: StorePickerOption) {
  return [
    option.name,
    option.address,
    option.city,
    option.chainName,
    option.storeCode,
  ].filter(Boolean).join(' ').toLocaleLowerCase();
}

function optionDescription(option: StorePickerOption) {
  return [option.address, option.city].filter(Boolean).join(' · ');
}

function optionSupportingText(option: StorePickerOption) {
  return option.chainName || option.storeCode || '';
}

export function StorePicker({
  allOptionLabel,
  ariaLabel,
  autoOpen = false,
  disabled = false,
  emptyMessage = 'No stores match this search.',
  isLoading = false,
  onChange,
  onSearchChange,
  options,
  pagination,
  placeholder = 'Search name, chain, code, address or city',
  required = false,
  selectedOption,
  value,
}: StorePickerProps) {
  const generatedId = useId().replaceAll(':', '');
  const listboxId = `store-picker-${generatedId}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [internalPage, setInternalPage] = useState(1);
  const [internalSearch, setInternalSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [popoverPosition, setPopoverPosition] = useState<PopoverPosition | null>(null);
  const [rememberedSelection, setRememberedSelection] = useState<StorePickerOption | null>(null);
  const isRemoteSearch = onSearchChange !== undefined;
  const search = internalSearch;
  const selected = selectedOption
    ?? options.find((option) => option.id === value)
    ?? (rememberedSelection?.id === value ? rememberedSelection : null);
  const matchingOptions = useMemo(() => {
    if (isRemoteSearch || !search.trim()) return options;
    const normalizedSearch = search.trim().toLocaleLowerCase();
    return options.filter((option) => searchableText(option).includes(normalizedSearch));
  }, [isRemoteSearch, options, search]);
  const pageCount = pagination?.pageCount ?? Math.max(1, Math.ceil(matchingOptions.length / RESULTS_PER_PAGE));
  const page = pagination?.page ?? Math.min(internalPage, pageCount);
  const visibleOptions = pagination
    ? matchingOptions
    : matchingOptions.slice((page - 1) * RESULTS_PER_PAGE, page * RESULTS_PER_PAGE);

  const positionPopover = useCallback(() => {
    const anchor = pickerRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const viewportMargin = 12;
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - viewportMargin;
    const spaceAbove = rect.top - viewportMargin;
    const opensAbove = spaceBelow < 220 && spaceAbove > spaceBelow;
    const availableHeight = Math.max(140, (opensAbove ? spaceAbove : spaceBelow) - gap);
    const popoverHeight = Math.min(330, availableHeight);
    setPopoverPosition({
      ...(opensAbove
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
      left: rect.left,
      resultsMaxHeight: Math.max(96, popoverHeight - (pageCount > 1 ? 44 : 0)),
      width: rect.width,
    });
  }, [pageCount]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!pickerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    const frame = window.requestAnimationFrame(positionPopover);
    document.addEventListener('mousedown', closeOnOutsideClick);
    window.addEventListener('resize', positionPopover);
    window.addEventListener('scroll', positionPopover, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('mousedown', closeOnOutsideClick);
      window.removeEventListener('resize', positionPopover);
      window.removeEventListener('scroll', positionPopover, true);
    };
  }, [isOpen, positionPopover]);

  useEffect(() => {
    if (!isOpen || selected) return undefined;
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, selected]);

  function setSearch(valueToSet: string) {
    setInternalSearch(valueToSet);
    setInternalPage(1);
    onSearchChange?.(valueToSet);
  }

  function changePage(nextPage: number) {
    setActiveIndex(0);
    if (pagination) {
      pagination.onPageChange(nextPage);
      return;
    }
    setInternalPage(nextPage);
  }

  function selectOption(option: StorePickerOption | null) {
    setRememberedSelection(option);
    onChange(option?.id ?? '');
    setSearch('');
    setIsOpen(false);
    setActiveIndex(0);
  }

  function openPicker() {
    if (disabled) return;
    setSearch('');
    setIsOpen(true);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      if (visibleOptions.length === 0) return;
      setActiveIndex((current) => (
        event.key === 'ArrowDown'
          ? (current + 1) % visibleOptions.length
          : (current - 1 + visibleOptions.length) % visibleOptions.length
      ));
      return;
    }
    if (event.key === 'Enter' && isOpen && visibleOptions[activeIndex]) {
      event.preventDefault();
      selectOption(visibleOptions[activeIndex]);
    }
  }

  const optionRows = allOptionLabel ? [null, ...visibleOptions] : visibleOptions;

  return (
    <div className="store-picker" ref={pickerRef}>
      {selected && !isOpen && !disabled ? (
        <div className="store-picker-selection">
          <span>
            <strong>{selected.name}</strong>
            {optionDescription(selected) ? <small>{optionDescription(selected)}</small> : null}
          </span>
          <button className="text-button" disabled={disabled} onClick={openPicker} type="button">Change</button>
        </div>
      ) : (
        <div className="store-picker-input">
          <Search aria-hidden="true" size={17} />
          <input
            aria-activedescendant={isOpen && visibleOptions[activeIndex] ? `${listboxId}-${visibleOptions[activeIndex].id}` : undefined}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-expanded={isOpen}
            aria-label={ariaLabel}
            aria-required={required || undefined}
            disabled={disabled}
            onClick={() => setIsOpen(true)}
            onChange={(event) => {
              setSearch(event.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            ref={inputRef}
            required={required}
            role="combobox"
            value={disabled && selected ? selected.name : search}
          />
        </div>
      )}
      {isOpen && popoverPosition ? createPortal(
        <div
          className="store-picker-popover"
          ref={popoverRef}
          style={{
            ...(popoverPosition.top === undefined ? {} : { top: popoverPosition.top }),
            ...(popoverPosition.bottom === undefined ? {} : { bottom: popoverPosition.bottom }),
            left: popoverPosition.left,
            width: popoverPosition.width,
          }}
        >
          <div className="store-picker-results" id={listboxId} role="listbox" style={{ maxHeight: popoverPosition.resultsMaxHeight }}>
            {isLoading ? <p><InlineSpinner /> Searching stores…</p> : null}
            {!isLoading && optionRows.length === 0 ? <p>{emptyMessage}</p> : null}
            {optionRows.map((option, index) => {
              if (option === null) {
                return <button aria-selected={!value} className={!value ? 'active' : ''} key="all-stores" onClick={() => selectOption(null)} role="option" type="button"><span><strong>{allOptionLabel}</strong></span></button>;
              }
              const optionIndex = allOptionLabel ? index - 1 : index;
              return (
                <button
                  aria-selected={optionIndex === activeIndex}
                  className={optionIndex === activeIndex ? 'active' : ''}
                  id={`${listboxId}-${option.id}`}
                  key={option.id}
                  onClick={() => selectOption(option)}
                  onMouseEnter={() => setActiveIndex(optionIndex)}
                  role="option"
                  type="button"
                >
                  <span>
                    <strong>{option.name}</strong>
                    {optionDescription(option) ? <small>{optionDescription(option)}</small> : null}
                  </span>
                  {optionSupportingText(option) ? <small>{optionSupportingText(option)}</small> : null}
                </button>
              );
            })}
          </div>
          {pageCount > 1 ? (
            <div className="store-picker-pagination">
              <button className="text-button" disabled={page <= 1} onClick={() => changePage(page - 1)} type="button">Previous</button>
              <span>Page {page} of {pageCount}</span>
              <button className="text-button" disabled={page >= pageCount} onClick={() => changePage(page + 1)} type="button">Next</button>
            </div>
          ) : null}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
